import { FINANCE_CONFIG } from '@financego/core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useI18n } from '../../i18n/useI18n';
import { buildCalendarProjection, type CalendarProjectionPoint } from './calendar-data';
import { formatLongDate, formatMoney, formatMonth } from './dashboard-format';
import type { DashboardSnapshot } from './dashboard.types';

export function CalendarView({ snapshot }: { readonly snapshot: DashboardSnapshot }) {
  const { locale, t } = useI18n();
  const weekdayLabels = [
    t('dashboard.weekdayMonday'),
    t('dashboard.weekdayTuesday'),
    t('dashboard.weekdayWednesday'),
    t('dashboard.weekdayThursday'),
    t('dashboard.weekdayFriday'),
    t('dashboard.weekdaySaturday'),
    t('dashboard.weekdaySunday'),
  ];
  const initial = new Date(`${snapshot.today}T00:00:00.000Z`);
  const [visibleMonth, setVisibleMonth] = useState({
    month: initial.getUTCMonth() + 1,
    year: initial.getUTCFullYear(),
  });
  const [selectedDate, setSelectedDate] = useState(snapshot.today);
  const points = useMemo(
    () => buildCalendarProjection(snapshot, visibleMonth.year, visibleMonth.month),
    [snapshot, visibleMonth],
  );
  const firstWeekday = getMondayBasedWeekday(visibleMonth.year, visibleMonth.month);
  const selectedPoint = points.find((point) => point.date === selectedDate) ?? points[0];
  const atMinimum =
    visibleMonth.year === FINANCE_CONFIG.projection.minYear && visibleMonth.month === 1;
  const atMaximum =
    visibleMonth.year === FINANCE_CONFIG.projection.maxYear && visibleMonth.month === 12;

  const moveMonth = (step: -1 | 1) => {
    const date = new Date(Date.UTC(visibleMonth.year, visibleMonth.month - 1 + step, 1));
    const next = { month: date.getUTCMonth() + 1, year: date.getUTCFullYear() };
    setVisibleMonth(next);
    setSelectedDate(`${String(next.year)}-${String(next.month).padStart(2, '0')}-01`);
  };

  return (
    <>
      <header className="dashboard-heading">
        <p className="eyebrow">{t('navigation.calendar')}</p>
        <h1>{t('dashboard.calendarTitle')}</h1>
        <p>{t('dashboard.calendarDescription')}</p>
      </header>

      <div className="calendar-layout">
        <section className="calendar-card" aria-labelledby="calendar-month-title">
          <div className="calendar-toolbar">
            <button
              className="icon-button"
              disabled={atMinimum}
              type="button"
              aria-label={t('dashboard.previousMonth')}
              onClick={() => moveMonth(-1)}
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <h2 id="calendar-month-title">
              {formatMonth(visibleMonth.year, visibleMonth.month, locale)}
            </h2>
            <button
              className="icon-button"
              disabled={atMaximum}
              type="button"
              aria-label={t('dashboard.nextMonth')}
              onClick={() => moveMonth(1)}
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
          <div className="calendar-weekdays" aria-hidden="true">
            {weekdayLabels.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
          <div className="calendar-grid">
            {Array.from({ length: firstWeekday }, (_, index) => (
              <span className="calendar-blank" key={`blank-${String(index)}`} />
            ))}
            {points.map((point, index) => (
              <CalendarDay
                day={index + 1}
                isSelected={selectedPoint?.date === point.date}
                isToday={point.date === snapshot.today}
                key={point.date}
                onSelect={() => setSelectedDate(point.date)}
                point={point}
              />
            ))}
          </div>
        </section>

        <section className="day-agenda" aria-labelledby="day-agenda-title">
          <div className="section-heading-row">
            <div>
              <p className="section-kicker">{t('dashboard.dailyAgenda')}</p>
              <h2 id="day-agenda-title">
                {formatLongDate(selectedPoint?.date ?? selectedDate, locale)}
              </h2>
            </div>
            <strong>
              {formatProjectedBalance(
                selectedPoint?.balance ?? null,
                locale,
                t('dashboard.unavailable'),
              )}
            </strong>
          </div>
          {selectedPoint?.movements.length === 0 || selectedPoint === undefined ? (
            <div className="empty-day">
              <p>{t('dashboard.noDayMovements')}</p>
            </div>
          ) : (
            <ul className="day-movements">
              {selectedPoint.movements.map((movement, index) => (
                <li key={`${movement.transactionId ?? movement.label}-${String(index)}`}>
                  <span
                    className={`movement-type-mark movement-type-${movement.type}`}
                    aria-hidden="true"
                  />
                  <div>
                    <strong>{movement.label}</strong>
                    <small>
                      {t(
                        movement.type === 'transfer'
                          ? 'dashboard.transferBetweenWallets'
                          : movement.type === 'income'
                            ? 'dashboard.projectedIncome'
                            : 'dashboard.projectedExpense',
                      )}
                    </small>
                  </div>
                  <span className={`movement-value movement-${movement.type}`}>
                    {movement.type === 'income' ? '+' : movement.type === 'expense' ? '−' : ''}
                    {formatMoney(movement.amount, locale)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

function CalendarDay({
  day,
  isSelected,
  isToday,
  onSelect,
  point,
}: {
  readonly day: number;
  readonly isSelected: boolean;
  readonly isToday: boolean;
  readonly onSelect: () => void;
  readonly point: CalendarProjectionPoint;
}) {
  const { locale, t } = useI18n();
  const movementCount = point.movements.length;
  return (
    <button
      aria-label={t('dashboard.calendarDayLabel', {
        balance: formatProjectedBalance(point.balance, locale, t('dashboard.unavailable')),
        count: movementCount,
        date: formatLongDate(point.date, locale),
      })}
      aria-pressed={isSelected}
      className={`${isSelected ? 'is-selected ' : ''}${isToday ? 'is-today ' : ''}${point.balance !== null && point.balance < 0 ? 'is-negative' : ''}`}
      onClick={onSelect}
      type="button"
    >
      <span className="calendar-day-number">{day}</span>
      {point.balance !== null && (
        <small className="calendar-day-balance" aria-hidden="true">
          {formatCompactBalance(point.balance, locale)}
        </small>
      )}
      {movementCount > 0 && <i aria-hidden="true">{Math.min(movementCount, 3)}</i>}
    </button>
  );
}

function formatCompactBalance(balance: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    compactDisplay: 'short',
    currency: 'USD',
    maximumFractionDigits: Math.abs(balance) < 1_000 ? 0 : 1,
    notation: 'compact',
    style: 'currency',
  }).format(balance);
}

function getMondayBasedWeekday(year: number, month: number): number {
  return (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
}

function formatProjectedBalance(
  balance: number | null,
  locale: string,
  unavailable: string,
): string {
  return balance === null ? unavailable : formatMoney(balance, locale);
}
